// Página admin de Meios de pagamento (superAdmin).
//
// A mesma listagem em duas VISÕES, que diferem só na dimensão do agrupamento:
// por meio de pagamento (padrão) e por white label. As abas nomeiam a lente, e
// não a idade do código — os dois agrupamentos são permanentes e igualmente
// atuais.
import { useTranslation } from "react-i18next"
import { Navigate, useSearchParams } from "react-router-dom"

import { AppBreadcrumb } from "@/components/layout/app-breadcrumb"
import { Header } from "@/components/layout/header"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { FRONTEND_ROUTES } from "@/constants"
import {
  ProcessorConfigPanelByMethod,
  ProcessorConfigPanelByWhiteLabel,
} from "@/features/processor-config"
import { useUrlTab } from "@/hooks/use-url-tab"

// A aba viaja na URL (?tab=), com valores que dizem o agrupamento: link direto
// sobrevive ao F5 e ao envio para um colega, e o valor continua legível para
// quem lê a URL. A default fica SEM o param.
const TAB_VALUES = ["by-method", "by-white-label"] as const
const DEFAULT_TAB = "by-method"

/**
 * Abas que já existiram e para onde os links salvos devem cair.
 *
 * `legacy` era a implementação antiga, agrupada POR WHITE LABEL — então o link
 * que um colega guardou entrega a organização que ele esperava, em vez de cair
 * calado na visão padrão.
 */
const RETIRED_TAB_ALIASES = new Map<string, string>([
  ["legacy", "by-white-label"],
])

export function ProcessorConfigPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [activeTab, handleTabChange] = useUrlTab(
    TAB_VALUES,
    DEFAULT_TAB
  )

  // Aba aposentada na URL: redireciona para a herdeira ANTES de renderizar,
  // preservando o resto da query string (o `?whiteLabel=` de um deep-link
  // viaja junto).
  const requestedTab = searchParams.get("tab")
  // Map, e não objeto literal: a chave vem da URL, e um lookup em objeto
  // resolveria `?tab=constructor` para algo do prototype — URL lixo a partir
  // de um parâmetro que qualquer um digita.
  const aliasTarget = requestedTab
    ? RETIRED_TAB_ALIASES.get(requestedTab)
    : undefined
  if (aliasTarget) {
    const next = new URLSearchParams(searchParams)
    next.set("tab", aliasTarget)
    return <Navigate replace to={`?${next.toString()}`} />
  }

  // O redirect de ?config= mora na PÁGINA, antes das abas: o link antigo
  // (`?config=GWC-...`) precisa levar ao detalhe independente de qual aba a
  // URL abriria — no painel antigo ele morreria sempre que a aba ativa fosse
  // a nova.
  const linkedConfigId = searchParams.get("config")
  if (linkedConfigId) {
    return (
      <Navigate
        replace
        to={FRONTEND_ROUTES.ADMIN_PAYMENT_METHOD_DETAIL.href.replace(
          ":configId",
          linkedConfigId
        )}
      />
    )
  }

  return (
    <main className="flex w-full flex-1 flex-col">
      <Header>
        <AppBreadcrumb
          links={[
            {
              href: FRONTEND_ROUTES.DASHBOARD.href,
              label: t("sidebar.dashboard"),
            },
            {
              label: t("sidebar.administration"),
            },
            {
              label: t("sidebar.paymentMethods"),
            },
          ]}
        />
      </Header>
      <section className="flex flex-1 flex-col gap-4 overflow-x-hidden p-4 @container">
        <div className="flex flex-col gap-1">
          <h1 className="page-title">
            {t("pages.adminProcessorConfig.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("pages.adminProcessorConfig.subtitle")}
          </p>
        </div>
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
        >
          <TabsList>
            <TabsTrigger value="by-method">
              {t(
                "pages.adminProcessorConfig.tabs.byMethod"
              )}
            </TabsTrigger>
            <TabsTrigger value="by-white-label">
              {t(
                "pages.adminProcessorConfig.tabs.byWhiteLabel"
              )}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="by-method" className="pt-2">
            <ProcessorConfigPanelByMethod />
          </TabsContent>
          <TabsContent
            value="by-white-label"
            className="pt-2"
          >
            <ProcessorConfigPanelByWhiteLabel />
          </TabsContent>
        </Tabs>
      </section>
    </main>
  )
}
